import { createApi } from "@reduxjs/toolkit/query/react";
import customFetchBase from "./customFetchBase";
import { getQueryParams } from "utils/getQueryParams";
import {
  IGenericResponse,
  IPaginatedList,
  IPaginationQuery,
} from "Interfaces/PaginatedList";
import { IPointOfSale } from "Models/PointOfSale";
import { PointsOfSaleFilters } from "Interfaces/PointsOfSaleFilters";

export const pointOfSaleApi = createApi({
  reducerPath: "pointOfSaleApi",
  baseQuery: customFetchBase,
  tagTypes: ["PointOfSale"],
  keepUnusedDataFor: 300,  // Keep cached data for 5 minutes globally
  refetchOnReconnect: true, // Refetch data when reconnected to the network
  refetchOnFocus: true, // Refetch data when the window is focused again
  endpoints: (builder) => ({
    getPointOfSale: builder.query<IPaginatedList<IPointOfSale>, IPaginationQuery<PointsOfSaleFilters>>({
      query(queryFilters) {

        return {
          url: getQueryParams(`point-of-sale`, queryFilters),
          credentials: "include",
        };
      },
      transformResponse: (results: IGenericResponse<IPaginatedList<IPointOfSale>>) =>
        results.data,
      providesTags: ["PointOfSale"],
    }),
    //get all point of sale
    getAllPointOfSale: builder.query<IPointOfSale[], void>({
      query() {
        return {
          url: "point-of-sale/get/all",
          method: "GET",
          credentials: "include",
        };
      },
      providesTags: () => [{ type: "PointOfSale" }],
      transformResponse: (results: { data: IPointOfSale[] }) => {
        return results.data;
      },
    }),

    // Get single user

    getAPieceOfPointOfSale: builder.query<IPointOfSale, string>({
      query(pointOfSaleId) {
        return {
          url: "point-of-sale/" + pointOfSaleId,
          method: "GET",
          credentials: "include",
        };
      },
      providesTags: ["PointOfSale"],
      transformResponse: (result: { data: IPointOfSale }) => {
        return result.data;
      },
    }),

    //GET all point of sale from a company
    getPointOfSaleByCompany: builder.query<IPointOfSale[], string>({
      query(companyId) {
        return {
          url:`point-of-sale/company/${companyId}`,
          method: "GET",
          credentials: "include",
        };
      },
      transformResponse: (results: {data:IPointOfSale[]}) =>
        results.data,
      providesTags: ["PointOfSale"],
    }),

    // Post user

    postPointOfSale: builder.mutation<IPointOfSale, FormData>({
      query(newPointOfSale) {
        return {
          url: "point-of-sale",
          method: "POST",
          body: newPointOfSale,
          credentials: "include",
        };
      },
      invalidatesTags: ["PointOfSale"],
      transformResponse: (result: { data:  IPointOfSale }) => {
        return result.data;
      },
    }),

    // Update user

    updatePointOfSale: builder.mutation<
      IPointOfSale,
      { id: string; pointOfSaleToUpdate: FormData }
    >({
      query({ id: pointOfSaleId, pointOfSaleToUpdate: updatedPointOfSale }) {
        return {
          url: "point-of-sale/" + pointOfSaleId,
          method: "PUT",
          body: updatedPointOfSale,
          credentials: "include",
        };
      },
      invalidatesTags: ["PointOfSale"],
      transformResponse: (result: { data: IPointOfSale }) => {
        return result.data;
      },
    }),

    // Delete user

    deletePointOfSale: builder.mutation<IPointOfSale, string>({
      query(pointOfSaleId) {
        return {
          url: "point-of-sale/" + pointOfSaleId,
          method: "DELETE",
          credentials: "include",
        };
      },
      invalidatesTags: ["PointOfSale"],
      transformResponse: (result: { data: IPointOfSale }) => {
        return result.data;
      },
    }),
  }),
});

export const {
  useGetAPieceOfPointOfSaleQuery,
  useGetPointOfSaleQuery,
  useGetPointOfSaleByCompanyQuery,
  useGetAllPointOfSaleQuery,
  usePostPointOfSaleMutation,
  useUpdatePointOfSaleMutation,
  useDeletePointOfSaleMutation,
} = pointOfSaleApi;
